function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Typography } from 'seagull';
import { billingStagesTextsPath } from "../../../../business-logic/billingLogic";
import BillingTriggerDropdown from "../../../../components/ServiceBuilder/BillingTriggerDropdown";
import OccurrenceTextField from "../../../../components/ServiceBuilder/OccurrenceTextField/OccurrenceTextField";
import PreapprovedTextField from "../../../../components/ServiceBuilder/PreapprovedTextField";
import PricingTextField from "../../../../components/ServiceBuilder/PricingTextField";
import { defaultInitialState } from "../../../../components/ServiceBuilder/ServiceBuilder";
import { serviceToInput } from "../../../../components/ServiceBuilder/ServiceBuilder.utils";
import UnitNameTextField from "../../../../components/ServiceBuilder/UnitNameTextField";
import { BillingSettingsSection } from "../../../../components/ServiceDetails/BillingSettingsSection/BillingSettingsSection";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { useLocaleKeys } from "../../../../i18n";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../../../models";
import { autoBillingPeriodLabel } from "../../../../utils/service-billing/automatic-service-billing-period";
import EditableTableCell from "../EditableTableCell/EditableTableCell";
import { BillingCellSubtitle } from "./BillingCellSubtitle/BillingCellSubtitle";
import { billingFormToService } from "./service.utils";
import styles from "./ServiceBillingCell.module.scss";
import { ServiceBillingModalContent } from "./ServiceBillingModalContent/ServiceBillingModalContent";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ServiceBillingContent = _ref => {
  let {
    draft,
    service,
    setDraft
  } = _ref;
  const {
    watch,
    getValues,
    setError,
    clearErrors
  } = useFormContext();
  const [pricing] = watch(['pricing']);
  if (!service) return null;
  watch(value => {
    const serviceNewData = _objectSpread(_objectSpread({}, getValues()), value);
    try {
      setDraft(draft.setService(service.id, s => billingFormToService({
        service: s,
        billingForm: serviceNewData
      })));
    } catch (err) {}
  });
  const onDayOfMonthChange = (serviceId, dayOfMonth) => {
    setDraft(draft.setService(service.id, s => serviceModel.setBillingDayOfMonth(s, dayOfMonth)));
  };
  const onDayOfWeekChange = (serviceId, dayOfWeek) => {
    setDraft(draft.setService(service.id, s => serviceModel.setBillingDayOfWeek(s, dayOfWeek)));
  };
  const onBillUpfrontModeChange = (serviceId, billUpfront) => {
    setDraft(draft.setService(service.id, s => serviceModel.setBillUpfront(s, billUpfront)));
  };
  const onDayOfYearChange = (serviceId, dayOfYear) => {
    setDraft(draft.setService(service.id, s => serviceModel.setBillingDayOfYear(s, dayOfYear)));
  };
  const onHasError = (serviceId, hasError) => {
    if (hasError) {
      setError('billingDayOfMonth', {
        message: ''
      });
    } else {
      clearErrors('billingDayOfMonth');
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: styles.serviceBillingCell,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.row,
      children: [/*#__PURE__*/_jsx(PricingTextField, {}), /*#__PURE__*/_jsx(OccurrenceTextField, {
        serviceOrigin: "draft",
        service: service
      })]
    }), ['hour', 'unit'].includes(pricing) && /*#__PURE__*/_jsxs("div", {
      className: styles.row,
      children: [pricing === 'unit' && /*#__PURE__*/_jsx(UnitNameTextField, {
        testId: "sb-unit-name"
      }), /*#__PURE__*/_jsx(PreapprovedTextField, {})]
    }), /*#__PURE__*/_jsx(BillingTriggerDropdown, {
      service: service,
      serviceOrigin: "draft",
      effectiveDate: draft.effectiveDate(),
      stagesClassName: styles.stages
    }), /*#__PURE__*/_jsx(BillingSettingsSection, {
      service: service,
      testId: "edit-service-billing-modal",
      isActiveAgreement: false,
      onDayOfMonthChange: onDayOfMonthChange,
      onDayOfWeekChange: onDayOfWeekChange,
      onDayOfYearChange: onDayOfYearChange,
      onBillUpfrontModeChange: onBillUpfrontModeChange,
      onHasError: onHasError
    })]
  });
};
export default function ServiceBillingCell(_ref2) {
  let {
    index,
    service,
    isDisabled,
    disabledTooltip,
    onOpenModal
  } = _ref2;
  const {
    START_BILLING_AT_PHASE_2_INLINE
  } = useFeatureFlags();
  const {
    t: billingStagesTexts
  } = useLocaleKeys(billingStagesTextsPath);
  const defaultValues = useMemo(() => service ? serviceToInput({
    service,
    qboItems: [],
    xeroItems: []
  }) : defaultInitialState, [service]);
  const form = useForm({
    mode: 'all',
    defaultValues
  });
  const onClick = () => {
    form.reset(defaultValues);
    onOpenModal({
      width: '640',
      renderContent: _ref3 => {
        let {
          draft,
          setDraft
        } = _ref3;
        return {
          title: 'Service billing',
          testId: "edit-service-billing-modal-".concat(index),
          onSubmit: defaultBehavior => {
            form.handleSubmit(defaultBehavior)();
          },
          content: START_BILLING_AT_PHASE_2_INLINE ? /*#__PURE__*/_jsx(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
            children: /*#__PURE__*/_jsx(ServiceBillingModalContent, {
              service: draft.service(service.id),
              draft: draft,
              setDraft: setDraft,
              testId: "edit-service-billing-modal-".concat(index)
            })
          })) : /*#__PURE__*/_jsx(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
            children: /*#__PURE__*/_jsx(ServiceBillingContent, {
              service: draft.service(service.id),
              draft: draft,
              setDraft: setDraft
            })
          }))
        };
      }
    });
  };
  const subtitle = useMemo(() => {
    if (serviceBillingModel.isAutomatic(service.billing)) {
      const label = autoBillingPeriodLabel(service.billing);
      return label ? label.text : undefined;
    }
    return serviceCostModel.stringifyServiceCost(service.cost).unitCapDisplay;
  }, [service.billing, service.cost]);
  return /*#__PURE__*/_jsxs(EditableTableCell, {
    testId: "edit-service-billing-cell-".concat(index),
    onClick: onClick,
    isDisabled: isDisabled,
    disabledTooltip: disabledTooltip,
    children: [/*#__PURE__*/_jsxs(Typography, {
      testId: "service-billing-".concat(index),
      children: [serviceModel.formatRecurrencePeriod(service), ' • ', serviceModel.formatBillingTrigger(service, billingStagesTexts)]
    }), START_BILLING_AT_PHASE_2_INLINE ? /*#__PURE__*/_jsx(BillingCellSubtitle, {
      service: service,
      testId: "service-billing-subtitle-".concat(index)
    }) : /*#__PURE__*/_jsx(Typography, {
      testId: "service-billing-subtitle-".concat(index),
      variant: "paragraph2",
      skin: "secondary",
      children: subtitle
    })]
  });
}